import { useState, useEffect, useCallback } from 'react';
import { useIntl } from 'react-intl';
import { Text, Button, Badge, Tab, TabList, Menu, MenuTrigger, MenuPopover, MenuList, MenuItem, MessageBar, MessageBarBody, MessageBarActions } from '@fluentui/react-components';
import { DocumentRegular, ArrowLeftRegular, ArrowDownloadRegular, CheckmarkCircleRegular, EditRegular, EyeRegular, ArrowSyncRegular, ErrorCircleRegular, DismissRegular } from '@fluentui/react-icons';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import '../components/MarkdownMessage.css';
import type { MethodologyDocument } from '../services/document-service';
import { updateDocumentStatus } from '../services/document-service';
import { listMigrateRuns } from '../services/migrate-service';
import { apiUrl } from '../utils/api-url';
import { useApiToken } from '../hooks/useApiToken';
import { getBadgeProps, DOC_STATUS_BADGE, getCompletenessColor } from '../utils/badge-config';

const STATUS_ICONS: Record<string, typeof EditRegular> = {
  draft: EditRegular,
  review: EyeRegular,
  final: CheckmarkCircleRegular,
};

const NEXT_STATUS: Record<string, string> = {
  draft: 'review',
  review: 'final',
};

interface DocumentViewerPageProps {
  document: MethodologyDocument;
  onBack: () => void;
  onDownloadPdf?: () => void;
  onStatusChange?: (docId: string, newStatus: string) => void;
}

export function DocumentViewerPage({ document, onBack, onDownloadPdf, onStatusChange }: DocumentViewerPageProps) {
  const intl = useIntl();
  const getToken = useApiToken();
  const [activeTab, setActiveTab] = useState('rendered');
  const [currentStatus, setCurrentStatus] = useState(document.status);
  const [updating, setUpdating] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [completeness, setCompleteness] = useState<number | null>(null);
  const [content, setContent] = useState(document.markdownContent);
  const [error, setError] = useState<string | null>(null);
  // Stale-data banner (RFC §4.6): set to the newer run's number when an imported run has
  // superseded the artifacts this document's render consumed. Never auto-regenerates.
  const [staleRunNumber, setStaleRunNumber] = useState<number | null>(null);
  const [staleDismissed, setStaleDismissed] = useState(false);

  useEffect(() => {
    // null/undefined = template consumes no migrate artifacts (or pre-PR-7 render) → never
    // banner. An EMPTY list means the template wanted artifacts but none existed at render
    // time — the first imported run supersedes that render.
    const consumed = document.consumedArtifacts;
    if (!consumed) return;
    let cancelled = false;
    (async () => {
      try {
        const token = await getToken();
        const runs = await listMigrateRuns(document.projectId, document.customerId, token);
        if (cancelled) return;
        // Runs list is runNumber-descending; run numbers are a project-total order. The
        // artifact filter MUST mirror the backend binder's anchor — a zero-row import is
        // 'imported' but carries no result artifact, and bannering on it would advertise a
        // regeneration that can never clear the banner.
        const newest = runs.find(r => r.status === 'imported' && r.resultArtifactId != null);
        if (!newest) return;
        const stale = consumed.length === 0
          || consumed.some(ref =>
            (ref.runNumber != null && ref.runNumber < newest.runNumber)
            // The customer-level estate summary is rebuilt on every ingest — any import
            // completed after it was rendered supersedes it. (Project-scoped check: a
            // sibling-project import is not visible here — accepted, the rendered block
            // self-discloses its as-at timestamp.)
            || (ref.artifactType === 'estate-summary' && ref.generatedAt != null
                && newest.completedAt != null && new Date(newest.completedAt) > new Date(ref.generatedAt)));
        if (stale) setStaleRunNumber(newest.runNumber);
      } catch {
        // Banner is best-effort advisory — a failed runs lookup must not break the viewer.
      }
    })();
    return () => { cancelled = true; };
  }, [document.consumedArtifacts, document.projectId, document.customerId, getToken]);

  const handleRegenerate = useCallback(async () => {
    setError(null);
    setRegenerating(true);
    try {
      const token = await getToken();
      const res = await fetch(apiUrl(`/api/documents/${document.id}/regenerate?customerId=${encodeURIComponent(document.customerId)}`), {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`Regenerate failed: ${res.status}`);
      const result = await res.json();
      setCompleteness(result.completeness);
      // Reload the document content
      const docRes = await fetch(apiUrl(`/api/documents/${document.id}?customerId=${encodeURIComponent(document.customerId)}`), {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (docRes.ok) {
        const updatedDoc = await docRes.json();
        setContent(updatedDoc.markdownContent);
      }
      // A successful regenerate consumed the newest run — the staleness is resolved.
      setStaleRunNumber(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Regenerate failed.');
    } finally {
      setRegenerating(false);
    }
  }, [document.id, document.customerId, getToken]);

  const handleStatusChange = async (newStatus: string, force = false) => {
    setUpdating(true);
    try {
      const token = await getToken();
      await updateDocumentStatus(document.id!, document.customerId, newStatus, token, force);
      setCurrentStatus(newStatus);
      onStatusChange?.(document.id!, newStatus);
    } catch (err) {
      console.error('Status update failed:', err);
    } finally {
      setUpdating(false);
    }
  };

  const nextStatus = NEXT_STATUS[currentStatus];
  const StatusIcon = STATUS_ICONS[currentStatus] ?? EditRegular;

  return (
    <div className="page-root page-root--scrollable document-viewer-page">
      <div className="page-title-bar">
        <Button appearance="subtle" icon={<ArrowLeftRegular />} onClick={onBack} title={intl.formatMessage({ id: 'documentViewer.backButton.title' })} />
        <DocumentRegular className="icon-title text-brand" />
        <Text size={500} weight="semibold">{document.title}</Text>
        <div className="title-bar-actions">
          {/* Status workflow controls */}
          <Menu>
            <MenuTrigger disableButtonEnhancement>
              <Button
                appearance="outline"
                size="small"
                icon={<StatusIcon />}
                disabled={updating}
              >
                <Badge size="small" {...getBadgeProps(DOC_STATUS_BADGE, currentStatus)}>{currentStatus}</Badge>
              </Button>
            </MenuTrigger>
            <MenuPopover>
              <MenuList>
                {nextStatus && (
                  <MenuItem icon={<CheckmarkCircleRegular />} onClick={() => handleStatusChange(nextStatus)}>
                    {intl.formatMessage({ id: 'documentViewer.status.promoteTo' }, { status: nextStatus })}
                  </MenuItem>
                )}
                {currentStatus === 'final' && (
                  <MenuItem icon={<EditRegular />} onClick={() => handleStatusChange('draft', true)}>
                    {intl.formatMessage({ id: 'documentViewer.status.revertToDraft' })}
                  </MenuItem>
                )}
                {currentStatus === 'review' && (
                  <MenuItem icon={<EditRegular />} onClick={() => handleStatusChange('draft', true)}>
                    {intl.formatMessage({ id: 'documentViewer.status.backToDraft' })}
                  </MenuItem>
                )}
              </MenuList>
            </MenuPopover>
          </Menu>

          <Button
            appearance="subtle"
            icon={<ArrowSyncRegular />}
            onClick={handleRegenerate}
            disabled={regenerating}
            size="small"
            title={intl.formatMessage({ id: 'documentViewer.regenerateButton.title' })}
          >
            {regenerating
              ? intl.formatMessage({ id: 'documentViewer.regenerateButton.regenerating' })
              : intl.formatMessage({ id: 'documentViewer.regenerateButton.idle' })}
          </Button>
          {completeness !== null && (
            <Badge size="small" color={getCompletenessColor(completeness)}>
              {intl.formatMessage({ id: 'documentViewer.completeness' }, { pct: Math.round(completeness * 100) })}
            </Badge>
          )}
          <Badge size="small" appearance="outline">{document.cafPhase}</Badge>
          <Badge size="small" appearance="outline">v{document.version}</Badge>
          {onDownloadPdf && (
            <Button appearance="subtle" icon={<ArrowDownloadRegular />} onClick={onDownloadPdf} title={intl.formatMessage({ id: 'documentViewer.downloadPdf.title' })}>
              {intl.formatMessage({ id: 'documentViewer.downloadPdf.label' })}
            </Button>
          )}
        </div>
      </div>

      {staleRunNumber !== null && !staleDismissed && (
        <MessageBar intent="warning">
          <MessageBarBody>
            {intl.formatMessage({ id: 'documentViewer.staleBanner.text' }, { run: staleRunNumber })}
          </MessageBarBody>
          <MessageBarActions
            containerAction={
              <Button
                appearance="transparent"
                size="small"
                icon={<DismissRegular />}
                onClick={() => setStaleDismissed(true)}
                title={intl.formatMessage({ id: 'documentViewer.staleBanner.dismiss' })}
              />
            }
          >
            <Button appearance="primary" size="small" onClick={handleRegenerate} disabled={regenerating}>
              {intl.formatMessage({ id: 'documentViewer.staleBanner.regenerate' })}
            </Button>
          </MessageBarActions>
        </MessageBar>
      )}

      {error && (
        <div className="page-error">
          <ErrorCircleRegular />
          <Text className="text-danger">{error}</Text>
          <Button appearance="primary" onClick={handleRegenerate}>{intl.formatMessage({ id: 'documentViewer.retryButton' })}</Button>
        </div>
      )}

      <div className="viewer-tabs">
        <TabList selectedValue={activeTab} onTabSelect={(_e, d) => setActiveTab(d.value as string)}>
          <Tab value="rendered">{intl.formatMessage({ id: 'documentViewer.tab.rendered' })}</Tab>
          <Tab value="markdown">{intl.formatMessage({ id: 'documentViewer.tab.markdown' })}</Tab>
        </TabList>
      </div>

      <div className="viewer-content">
        {activeTab === 'rendered' && (
          <div className="markdown-body">
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={{
                img: ({ ...props }) => (
                  <img {...props} className="markdown-body-img" />
                ),
                a: ({ href, children }) => (
                  <a href={href} target="_blank" rel="noopener noreferrer">
                    {children}
                  </a>
                ),
              }}
            >
              {content}
            </ReactMarkdown>
          </div>
        )}
        {activeTab === 'markdown' && (
          <pre className="markdown-source">{document.markdownContent}</pre>
        )}
      </div>
    </div>
  );
}
